import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getTenantInvitation } from '../services/supabase/tenantInvitations';

/**
 * Hook to detect user's role for a specific property
 * Determines if user is:
 * - Owner (created the property)
 * - Tenant (invited via tenant_invitations)
 * - Both (edge case: owner invites themselves)
 * - Neither (no access)
 *
 * @param {string} propertyId - Property UUID
 * @param {Object} property - Optional property object (if already loaded)
 * @returns {Object} Role information
 */
export function usePropertyRole(propertyId, property = null) {
  const { user, profile } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!propertyId || !user) {
      setLoading(false);
      return;
    }

    async function loadTenantInvitation() {
      try {
        setLoading(true);
        setError(null);

        // Check if user has tenant invitation for this property
        const inv = await getTenantInvitation(propertyId, user.id);
        setInvitation(inv);
      } catch (err) {
        console.error('Error loading tenant invitation:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadTenantInvitation();
  }, [propertyId, user?.id]);

  // Determine if user is owner
  const isOwner = property?.user_id === profile?.id;

  // Determine if user is tenant (has accepted invitation)
  const isTenant = invitation?.status === 'accepted';

  // Role determination
  let role = null;
  if (isOwner && isTenant) {
    role = 'both'; // Edge case: owner invited themselves
  } else if (isOwner) {
    role = 'owner';
  } else if (isTenant) {
    role = 'tenant';
  }

  return {
    role,
    isOwner,
    isTenant,
    isDualRole: isOwner && isTenant,
    permissions: invitation?.permissions || null,
    invitation,
    loading,
    error,
  };
}

/**
 * Lightweight version that doesn't fetch invitation
 * Use when you already know the property and just need quick role check
 *
 * @param {Object} property - Property object with user_id
 * @returns {Object} Basic role information
 */
export function usePropertyRoleSync(property) {
  const { profile } = useAuth();

  const isOwner = property?.user_id === profile?.id;

  return {
    isOwner,
    role: isOwner ? 'owner' : null,
  };
}
